import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { QUERY_KEYS } from '@/constants/query-keys';

import {
  createMembershipPackage,
  createPtPackage,
  listMembershipPackages,
  listPtPackages,
  setMembershipPackageActive,
  setPtPackageActive,
  updateMembershipPackage,
  updatePtPackage,
} from '../api/packages.api';
import type { MembershipPackageInput, PtPackageInput } from '../schemas/package.schema';

const ALL_FILTER = { scope: 'all' } as const;

/** Every membership plan, active and disabled — filter on the client, the list is small. */
export function useMembershipPackages({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: QUERY_KEYS.packages.membership(ALL_FILTER),
    queryFn: ({ signal }) => listMembershipPackages(signal),
    select: (page) => page.items,
    enabled,
  });
}

export function usePtPackages() {
  return useQuery({
    queryKey: QUERY_KEYS.packages.pt(ALL_FILTER),
    queryFn: ({ signal }) => listPtPackages(signal),
    select: (page) => page.items,
  });
}

function useInvalidatePackages() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.packages.all });
}

export function useSaveMembershipPackage() {
  const invalidate = useInvalidatePackages();
  return useMutation({
    mutationFn: ({ id, input }: { id: number | null; input: MembershipPackageInput }) =>
      id === null ? createMembershipPackage(input) : updateMembershipPackage(id, input),
    onSuccess: invalidate,
  });
}

export function useSavePtPackage() {
  const invalidate = useInvalidatePackages();
  return useMutation({
    mutationFn: ({ id, input }: { id: number | null; input: PtPackageInput }) =>
      id === null ? createPtPackage(input) : updatePtPackage(id, input),
    onSuccess: invalidate,
  });
}

export function useSetPackageActive() {
  const invalidate = useInvalidatePackages();
  return useMutation({
    mutationFn: async ({
      kind,
      id,
      isActive,
    }: {
      kind: 'membership' | 'pt';
      id: number;
      isActive: boolean;
    }): Promise<{ name: string; isActive: boolean }> =>
      kind === 'membership'
        ? setMembershipPackageActive(id, isActive)
        : setPtPackageActive(id, isActive),
    onSuccess: invalidate,
  });
}
